import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { MaterialReactTable, useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { TextField, Button, Tooltip } from '@mui/material';

import * as XLSX from 'xlsx';
import { useApplicationStore } from '../../stores/applicationStore';
import moment from 'moment';
import { VisaApplication } from '../../models/VisaApplication';
import { FileService } from '../../services/FileService';
import encryptionHelper from '../../helpers/encryptionHelper';
import { DBFile } from '../../models/db/DBFile';
import ApplicationStatusModal from '../../components/modals/ApplicationStatusModal';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

interface ApplicationsProps {
  isApproved?: boolean;
}

const Applications: FC<ApplicationsProps> = ({ isApproved }: ApplicationsProps) => {
  const applicationStore = useApplicationStore();
  const [applications, setApplications] = useState<any[]>([])
  const fileService = new FileService();
  const { t } = useTranslation(); // useTranslation hook'unu kullanarak 't' fonksiyonuna eriş

  useEffect(() => {
    if (applicationStore.applications.length > 0) {
      let _applications: any[] = applicationStore.applications;
      _applications = _(_applications).reverse().map((application, index) => ({
        ...application,
        index: index + 1,
      }))
        .sortBy('createdAt', 'desc')
        .value();

      if (isApproved) {
        _applications = _applications.filter((application) => application.status === 'approved');
      } else {
        // _applications = _applications.filter((application) => application.status !== 'approved');
      }

      setApplications(_applications);
      setFilteredData(_applications);
    }
  }, [applicationStore.applications]);


  // Silme işlemi fonksiyonu
  const handleDelete = (appID: string) => {
    console.log(`Kullanıcı ${appID} silindi!`);
    // Burada silme işlemi gerçekleştirilebilir
  };

  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [filteredData, setFilteredData] = useState<any[]>([]);


  //should be memoized or stable
  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        header: t('appsDataTable.basicInfo'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'index',
            header: 'ID',
            size: 50,
            enablePinning: true,  // Sabitlenmeyi etkinleştir
            pinned: 'left',       // Bu sütunu sola sabitle
            Cell: ({ cell }) => (
              <button
                className='badge text-bg-primary py-2 border-0'
                onClick={() => {
                  applicationStatusModal.current?.show(cell.row.original);
                }}
              >
                #{cell.getValue<string>()}
              </button>
            ),
            // Arama sırasında `index` alanındaki değeri kontrol etmesi için özel filtre işlevi
            filterFn: (row, columnId, filterValue) => {
              const cellValue = row.getValue(columnId);
              // `cellValue` tanımlıysa kontrol yapar, aksi takdirde `false` döner
              return cellValue ? cellValue.toString().includes(filterValue) : false;
            }
          },
          {
            accessorKey: 'download',
            header: t('files'),
            size: 50,
            enablePinning: true,  // Sabitlenmeyi etkinleştir
            pinned: 'left',       // Bu sütunu sola sabitle
            Cell: ({ cell }) => (
              <button className='badge text-bg-primary py-2 border-0' onClick={() => {
                const application = new VisaApplication(cell.row.original);
                const files = application.files();
                fileService.downloadAndZipFiles(cell.row.original.id, files);
              }}>{t('download')}</button>
            ),
          },
          {
            accessorKey: 'basicInformation.userFullName',
            header: t('appsDataTable.fullname'),  // Ad Soyad
            size: 200,
            enablePinning: true,  // Sabitlenmeyi etkinleştir
            pinned: 'left',       // Bu sütunu sola sabitle
          },
          {
            accessorKey: 'createdAt',
            header: t('appsDataTable.date'),
            size: 200,
            // Cell: ({ cell }) => moment(cell.getValue<Date>()).format('DD/MM/YYYY'), // Formatlama
            Cell: ({ cell }) => moment(cell.getValue<Date>()).format('DD/MM/YYYY HH:mm'), // Tarih ve saat formatlama

          },
          {
            accessorKey: 'basicInformation.userPhone',
            header: t('appsDataTable.phone'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.userEmail',
            header: t('appsDataTable.email'),
            size: 120,
          },
          {
            accessorKey: 'status',
            header: t('appsDataTable.status'),
            size: 120,
            Cell: ({ cell }) => {
              const status = cell.getValue<string>();
              let badgeClass = 'badge text-bg-primary py-2';
              let label = "";
              switch (status) {
                case 'pending':
                  badgeClass = 'badge text-bg-warning py-2';
                  label = t('pending')
                  break;
                case 'approved':
                  label = t('PreApplicationApproved')
                  badgeClass = 'badge text-bg-success py-2';
                  break;
                case 'rejected':
                  label = t('PreApplicationRejected')
                  badgeClass = 'badge text-bg-danger py-2';
                  break;
                case 'missing_documents':
                  label = t('MissingDocuments')
                  badgeClass = 'badge text-bg-info py-2';
                  break;
                case 'visa_approved':
                  label = t('VisaApproved')
                  badgeClass = 'badge text-bg-success py-2';
                  break;
                case 'visa_rejected':
                  label = t('VisaRejected')
                  badgeClass = 'badge text-bg-danger py-2';
                  break;
                default:
                  badgeClass = 'badge text-bg-secondary py-2';
              }
              return (
                <span className={badgeClass}>{label}</span>
              );
              
            },
            filterFn: (row, columnId, filterValue) => {
              const cellValue = row.getValue(columnId);
              return cellValue ? cellValue.toString().includes(filterValue) : false;
            }
          },
          {
            accessorKey: 'visaType',
            header: t('appsDataTable.visaType'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.residenceCard',
            header: t('appsDataTable.residenceCard'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.residenceCardCopy',
            header: t('appsDataTable.residenceCardCopy'),
            size: 250,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'basicInformation.note',
            header: t('appsDataTable.note'),
            size: 120,
          },
        ],
      },
      {
        header: t('appsDataTable.IdentityInformation'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'basicInformation.mothersName',
            header: t('appsDataTable.mothersName'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.fathersName',
            header: t('appsDataTable.fathersName'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.gender',
            header: t('appsDataTable.gender'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.nationality',
            header: t('appsDataTable.nationality'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.citizenship',
            header: t('appsDataTable.citizenship'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.maidenName',
            header: t('appsDataTable.maidenName'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.birthDate',
            header: t('appsDataTable.birthDate'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.birthPlace',
            header: t('appsDataTable.birthPlace'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.city',
            header: t('appsDataTable.city'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.country',
            header: t('appsDataTable.country'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.address',
            header: t('appsDataTable.address'),
            size: 120,
          },
          {
            accessorKey: 'basicInformation.maritalStatus',
            header: t('appsDataTable.maritalStatus'),
            size: 120,
          },
        ],
      },
      {
        header: t('appsDataTable.passportInfo'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'passportInformation.passportType',
            header: t('appsDataTable.passportType'),
            size: 120,
          },
          {
            accessorKey: 'passportInformation.passportNumber',
            header: t('appsDataTable.passportNumber'),
            size: 120,
          },
          {
            accessorKey: 'passportInformation.passportDate',
            header: t('appsDataTable.passportDate'),
            size: 280,
          },
          {
            accessorKey: 'passportInformation.passportEndDate',
            header: t('appsDataTable.passportEndDate'),
            size: 260,
          },
          {
            accessorKey: 'passportInformation.passportCopy',
            header: t('appsDataTable.passportCopy'),
            size: 120,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
        ],
      },
      {
        header: t('appsDataTable.VisaInfo'),
        columns: [ // Alt sütunlar
          {
            accessorKey: 'visaInfo.visitReason',
            header: t('appsDataTable.visitReason'),
            size: 120,
          },
          {
            accessorKey: 'visaInfo.stayAddress',
            header: t('appsDataTable.stayAddress'),
            size: 120,
          },
          {
            accessorKey: 'visaInfo.visaNumber',
            header: t('appsDataTable.visaNumber'),
            size: 120,
          },
          {
            accessorKey: 'visaInfo.visaDate',
            header: t('appsDataTable.visaDate'),
            size: 120,
          },
          {
            accessorKey: 'visaInfo.visaAddress',
            header: t('appsDataTable.visaAddress'),
            size: 120,
          },
          {
            accessorKey: 'visaInfo.appointmentDate',
            header: t('appsDataTable.appointmentDate'),
            size: 250,
          },
          // {
          //   accessorKey: 'visaInfo.copyOfPreVisas',
          //   header: 'Önceki Vize Kopyaları',
          //   size: 120,
          // },
        ],
      },
      {
        header: t('appsDataTable.businessVisa'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'businessVisaInformation.businessTurkishCompanyName',
            header: t('appsDataTable.businessTurkishCompanyName'),
            size: 280,
          },
          {
            accessorKey: 'businessVisaInformation.businessTurkishCompanyAddress',
            header: t('appsDataTable.businessTurkishCompanyAddress'),
            size: 280,
          },
          {
            accessorKey: 'businessVisaInformation.businessAlgeriaCompanyName',
            header: t('appsDataTable.businessAlgeriaCompanyName'),
            size: 280,
          },
          {
            accessorKey: 'businessVisaInformation.businessAlgeriaCompanyAddress',
            header: t('appsDataTable.businessAlgeriaCompanyAddress'),
            size: 280,
          },
          {
            accessorKey: 'businessVisaInformation.businessJob',
            header: t('appsDataTable.businessJob'),
            size: 100,
          },
          {
            accessorKey: 'businessVisaInformation.businessTitle',
            header: t('appsDataTable.businessTitle'),
            size: 120,
          },
          {
            accessorKey: 'businessVisaInformation.businessVisaDuration',
            header: t('appsDataTable.businessVisaDuration'),
            size: 100,
          },
          {
            accessorKey: 'businessVisaInformation.businessVisaStartDate',
            header: t('appsDataTable.businessVisaStartDate'),
            size: 230,
          },
          {
            accessorKey: 'businessVisaInformation.sgkServiceStatement',
            header: t('appsDataTable.sgkServiceStatement'),
            size: 230,
          },
          {
            accessorKey: 'businessVisaInformation.businessCompanyInvitation',
            header: t('appsDataTable.businessCompanyInvitation'),
            size: 120,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'businessVisaInformation.businessUndertakingLetter',
            header: t('appsDataTable.businessUndertakingLetter'),
            size: 340,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'businessVisaInformation.businessAlgerianCompanyDocs',
            header: t('appsDataTable.businessAlgerianCompanyDocs'),
            size: 250,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'businessVisaInformation.businessTurkishCompanyDocs',
            header: t('appsDataTable.businessTurkishCompanyDocs'),
            size: 220,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
        ],
      },
      {
        header: t('appsDataTable.workVisa'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'workVisaInformation.workAlgeriaCompanyName',
            header: t('appsDataTable.workAlgeriaCompanyName'),
            size: 280,
          },
          {
            accessorKey: 'workVisaInformation.workAlgeriaCompanyAddress',
            header: t('appsDataTable.workAlgeriaCompanyAddress'),
            size: 280,
          },
          {
            accessorKey: 'workVisaInformation.workJob',
            header: t('appsDataTable.workJob'),
            size: 120,
          },
          {
            accessorKey: 'workVisaInformation.workTitle',
            header: t('appsDataTable.workTitle'),
            size: 120,
          },
          {
            accessorKey: 'workVisaInformation.workVisaDuration',
            header: t('appsDataTable.workVisaDuration'),
            size: 120,
          },
          {
            accessorKey: 'workVisaInformation.workVisaStartDate',
            header: t('appsDataTable.workVisaStartDate'),
            size: 230,
          },
          {
            accessorKey: 'workVisaInformation.workVisaDocs',
            header: t('appsDataTable.workVisaDocs'),
            size: 250,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
        ],
      },
      {
        header: 'Aile Vizesi', // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'familyVisaInformation.visaSupporterClosenessDegree',
            header: t('appsDataTable.visaSupporterClosenessDegree'),
            size: 260,
          },
          {
            accessorKey: 'familyVisaInformation.visaSupporterFullName',
            header: t('appsDataTable.visaSupporterFullName'),
            size: 220,
          },
          {
            accessorKey: 'familyVisaInformation.supporterAlgIDNumber',
            header: t('appsDataTable.supporterAlgIDNumber'),
            size: 290,
          },
          {
            accessorKey: 'familyVisaInformation.supporterBirthDate',
            header: t('appsDataTable.supporterBirthDate'),
            size: 240,
          },
          {
            accessorKey: 'familyVisaInformation.supporterNationality',
            header: t('appsDataTable.supporterNationality'),
            size: 120,
          },
          {
            accessorKey: 'familyVisaInformation.familyVisaDuration',
            header: t('appsDataTable.familyVisaDuration'),
            size: 120,
          },
          {
            accessorKey: 'familyVisaInformation.visaStartDate',
            header: t('appsDataTable.visaStartDate'),
            size: 220,
          },
          {
            accessorKey: 'familyVisaInformation.marriageCertificateCopy',
            header: t('appsDataTable.marriageCertificateCopy'),
            size: 250,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'familyVisaInformation.civilRecords',
            header: t('appsDataTable.civilRecords'),
            size: 260,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'familyVisaInformation.birthCertificate',
            header: t('appsDataTable.birthCertificate'),
            size: 220,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'familyVisaInformation.supporterPassportCopy',
            header: t('appsDataTable.supporterPassportCopy'),
            size: 250,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'familyVisaInformation.supporterIDCopy',
            header: t('appsDataTable.supporterIDCopy'),
            size: 250,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'familyVisaInformation.familyInvitation',
            header: t('appsDataTable.familyInvitation'),
            size: 180,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'familyVisaInformation.kinshipCertificate',
            header: t('appsDataTable.kinshipCertificate'),
            size: 160,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
        ],
      },
      {
        header: t('appsDataTable.touristVisa'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'touristVisaInformation.roundTripTicket',
            header: t('appsDataTable.roundTripTicket'),
            size: 240,
          },
          {
            accessorKey: 'touristVisaInformation.travelHealthInsurance',
            header: t('appsDataTable.travelHealthInsurance'),
            size: 300,
          },
          {
            accessorKey: 'touristVisaInformation.touristVisaStartDate', // ömer not: bu alan görünmüyor / tanımsız
            header: t('appsDataTable.touristVisaStartDate'),
            size: 290,
          },
          {
            accessorKey: 'touristVisaInformation.invitationStatus',
            header: t('appsDataTable.invitationStatus'),
            size: 120,
          },
          {
            accessorKey: 'touristVisaInformation.InviterIsFullName',  // ömer not: bu alan görünmüyor / tanımsız
            header: t('appsDataTable.InviterIsFullName'),
            size: 120,
          },
          {
            accessorKey: 'touristVisaInformation.InviterIDNumber',
            header: t('appsDataTable.InviterIDNumber'),
            size: 120,
          },
          {
            accessorKey: 'touristVisaInformation.hotelReservation',
            header: t('appsDataTable.hotelReservation'),
            size: 260,
          },
          {
            accessorKey: 'touristVisaInformation.hotelInfo',
            header: t('appsDataTable.hotelInfo'),
            size: 220,
          },
          {
            accessorKey: 'touristVisaInformation.travelVisaDuration',
            header: t('appsDataTable.travelVisaDuration'),
            size: 230,
          },
          {
            accessorKey: 'touristVisaInformation.proofOfEmploymentOrStudentCer',
            header: t('appsDataTable.proofOfEmploymentOrStudentCer'),
            size: 340,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'touristVisaInformation.proofOfIncome',
            header: t('appsDataTable.proofOfIncome'),
            size: 320,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'touristVisaInformation.hotelReservationOrInvitationCopy',
            header: t('appsDataTable.hotelReservationOrInvitationCopy'),
            size: 320,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'touristVisaInformation.InviterIDCopy',
            header: t('appsDataTable.InviterIDCopy'),
            size: 280,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'touristVisaInformation.travelHealthInsuranceCopy',
            header: t('appsDataTable.travelHealthInsuranceCopy'),
            size: 280,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
          {
            accessorKey: 'touristVisaInformation.roundTripTicketCopy',
            header: t('appsDataTable.roundTripTicketCopy'),
            size: 280,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
        ],
      },
      {
        header: t('appsDataTable.transitVisa'), // Grup başlığı
        columns: [ // Alt sütunlar
          {
            accessorKey: 'transitVisaInformation.destinationCountry', // ömer not: görünmüyor
            header: t('appsDataTable.destinationCountry'),
            size: 190,
          },
          {
            accessorKey: 'transitVisaInformation.destinationCountryVisa', // ömer not: görünmüyor
            header: t('appsDataTable.destinationCountryVisa'),
            size: 290,
          },
          {
            accessorKey: 'transitVisaInformation.transitVisaStartDate',
            header: t('appsDataTable.transitVisaStartDate'),
            size: 240,
          },
          {
            accessorKey: 'transitVisaInformation.destinationCountryVisaCopy',
            header: t('appsDataTable.destinationCountryVisaCopy'),
            size: 300,
            Cell: ({ cell }) => {
              const files = cell.getValue<DBFile[]>();

              if (!files || files.length === 0) {
                return null;
              }

              return (
                <>
                  {files.map((file, index) => (
                    <a
                      key={file.path}
                      href={`${process.env.REACT_APP_BASE_URL}/file/${encryptionHelper.encryptBase64(file.path)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className='btn btn-sm btn-success me-1'
                      style={{ padding: '2px 4px' }}
                    >
                      {`#${index + 1}`}
                    </a>
                  ))}
                </>
              );
            }
          },
        ],
      },
    ],
    [],
  );


  // Excel Dışa Aktarma Fonksiyonu
  const handleExportExcel = () => {
    const data = applications.map((application) => new VisaApplication(application).toPlainObject());
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'ApplicationList');
    XLSX.writeFile(workbook, 'ApplicationList.xlsx');
  };

  const handleFilter = () => {
    const filteredData = applications.filter((application) => moment(application.createdAt).isBetween(moment(startDate), moment(endDate).add(1, 'days')));
    setFilteredData(filteredData);
  };

  const applicationStatusModal = useRef<{
    show: (application: any) => void,
    close: () => void,
  }>();

  return (
    <>
      <ApplicationStatusModal ref={applicationStatusModal} />
      <div className="section title-section">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title d-flex justify-content-start align-items-center">
                <div className="title-icon"><i className="bi bi-chat-left-dots"></i></div>
                <div className="title-content">
                  <h1>{t('applications')}</h1>
                  <p>{t('filterApplications')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="section section-filter my-4">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card-block card-block-02">
                <h2 className="card-title">{t('searchApplication')}</h2>
                <div className="card-content">
                  <div className="row">
                    <div className='d-flex'>
                      <TextField
                        type="date"
                        label={t('startDate')}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        type="date"
                        label={t('endDate')}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        style={{ marginLeft: '1rem' }}
                      />
                      <button className='btn btn-success'
                        onClick={handleFilter}
                        style={{ marginLeft: '1rem' }}
                      >
                        {t('filter')}
                      </button>
                    </div>
                    <MaterialReactTable
                      columns={columns}
                      data={filteredData}
                      enableColumnPinning={true}
                      initialState={{
                        columnPinning: {
                          left: ['index', 'basicInformation.userFullName'],
                        },
                        columnVisibility: {
                          // Temel Veriler
                          'index': true,
                          'download': true,
                          'basicInformation.userFullName': true,
                          'createdAt': true,
                          'basicInformation.userPhone': true,
                          'basicInformation.userEmail': true,
                          'status': true,
                          'visaType': true,
                          'basicInformation.residenceCard': false,
                          'basicInformation.residenceCardCopy': false,
                          'basicInformation.note': false,

                          // Kimlik Bilgileri
                          'basicInformation.mothersName': false,
                          'basicInformation.fathersName': false,
                          'basicInformation.gender': false,
                          'basicInformation.nationality': false,
                          'basicInformation.citizenship': false,
                          'basicInformation.maidenName': false,
                          'basicInformation.birthDate': false,
                          'basicInformation.birthPlace': false,
                          'basicInformation.city': false,
                          'basicInformation.country': false,
                          'basicInformation.address': false,
                          'basicInformation.maritalStatus': false,

                          // Pasaport Bilgileri
                          'passportInformation.passportType': false,
                          'passportInformation.passportNumber': false,
                          'passportInformation.passportDate': false,
                          'passportInformation.passportEndDate': false,
                          'passportInformation.passportCopy': false,

                          // Vize Bilgileri
                          'visaInfo.visitReason': false,
                          'visaInfo.stayAddress': false,
                          'visaInfo.visaNumber': false,
                          'visaInfo.visaDate': false,
                          'visaInfo.visaAddress': false,
                          'visaInfo.appointmentDate': false,
                          // 'visaInfo.copyOfPreVisas': false,

                          // Ticari Vize
                          'businessVisaInformation.businessTurkishCompanyName': false,
                          'businessVisaInformation.businessTurkishCompanyAddress': false,
                          'businessVisaInformation.businessAlgeriaCompanyName': false,
                          'businessVisaInformation.businessAlgeriaCompanyAddress': false,
                          'businessVisaInformation.businessJob': false,
                          'businessVisaInformation.businessTitle': false,
                          'businessVisaInformation.businessVisaDuration': false,
                          'businessVisaInformation.businessVisaStartDate': false,
                          'businessVisaInformation.sgkServiceStatement': false,
                          'businessVisaInformation.businessCompanyInvitation': false,
                          'businessVisaInformation.businessUndertakingLetter': false,
                          'businessVisaInformation.businessAlgerianCompanyDocs': false,
                          'businessVisaInformation.businessTurkishCompanyDocs': false,

                          // Çalışma Vizesi
                          'workVisaInformation.workAlgeriaCompanyName': false,
                          'workVisaInformation.workAlgeriaCompanyAddress': false,
                          'workVisaInformation.workJob': false,
                          'workVisaInformation.workTitle': false,
                          'workVisaInformation.workVisaDuration': false,
                          'workVisaInformation.workVisaStartDate': false,
                          'workVisaInformation.workVisaDocs': false,

                          // Aile Vizesi
                          'familyVisaInformation.visaSupporterClosenessDegree': false,
                          'familyVisaInformation.visaSupporterFullName': false,
                          'familyVisaInformation.supporterAlgIDNumber': false,
                          'familyVisaInformation.supporterBirthDate': false,
                          'familyVisaInformation.supporterNationality': false,
                          'familyVisaInformation.supporterPassportCopy': false,
                          'familyVisaInformation.supporterIDCopy': false,
                          'familyVisaInformation.visaStartDate': false,
                          'familyVisaInformation.marriageCertificateCopy': false,
                          'familyVisaInformation.civilRecords': false,
                          'familyVisaInformation.birthCertificate': false,
                          'familyVisaInformation.familyInvitation': false,
                          'familyVisaInformation.kinshipCertificate': false,
                          'familyVisaInformation.familyVisaDuration': false,

                          // Turistik Vize
                          'touristVisaInformation.roundTripTicket': false,
                          'touristVisaInformation.travelHealthInsurance': false,
                          'touristVisaInformation.invitationStatus': false,
                          'touristVisaInformation.InviterIsFullName': false,
                          'touristVisaInformation.InviterIDNumber': false,
                          'touristVisaInformation.hotelReservation': false,
                          'touristVisaInformation.hotelInfo': false,
                          'touristVisaInformation.proofOfEmploymentOrStudentCer': false,
                          'touristVisaInformation.proofOfIncome': false,
                          'touristVisaInformation.touristVisaStartDate': false,
                          'touristVisaInformation.hotelReservationOrInvitationCopy': false,
                          'touristVisaInformation.InviterIDCopy': false,
                          'touristVisaInformation.travelHealthInsuranceCopy': false,
                          'touristVisaInformation.roundTripTicketCopy': false,
                          'touristVisaInformation.travelVisaDuration': false,

                          // Transit Vize
                          'transitVisaInformation.destinationCountry': false,
                          'transitVisaInformation.destinationCountryVisa': false,
                          'transitVisaInformation.transitVisaStartDate': false,
                          'transitVisaInformation.destinationCountryVisaCopy': false,
                        },
                        sorting: [{ id: 'createdAt', desc: true }], // Varsayılan olarak en güncel tarihe göre sıralama
                      }}
                    />
                    <div className='d-flex justify-content-start'>
                      <button className='btn btn-primary' onClick={handleExportExcel}><i className="bi bi-cloud-arrow-down-fill me-2"></i>{t('export')}</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Applications;
